import { useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { useOnboardingState } from 'state/useOnboardingState';

type UseCriticalAlertsOnboarding = {
  shouldShowCriticalAlertsStep: (notificationsEnabled: boolean) => boolean;
  isCriticalAlertsEnabled: boolean;
  /**
   * Platform + flag check for Critical Alerts, ignoring whether the user has
   * already been prompted. Useful for stable signals like onboarding progress totals.
   */
  isCriticalAlertsAvailable: boolean;
};

export const useCriticalAlertsOnboarding = (): UseCriticalAlertsOnboarding => {
  const isCriticalAlertsEnabled = useReleaseFlag('enable_critical_alerts');
  const { criticalAlertsRequested } = useOnboardingState();

  const isCriticalAlertsAvailable =
    isCriticalAlertsEnabled && Capacitor.getPlatform() === 'ios';

  const shouldShowCriticalAlertsStep = useCallback(
    (notificationsEnabled: boolean) => {
      return (
        notificationsEnabled &&
        isCriticalAlertsAvailable &&
        !criticalAlertsRequested
      );
    },
    [isCriticalAlertsAvailable, criticalAlertsRequested],
  );

  return {
    shouldShowCriticalAlertsStep,
    isCriticalAlertsEnabled,
    isCriticalAlertsAvailable,
  };
};
